// ----------------------------------------------------------------------------
// 📄 APP.TSX - Main Application Entry (Worship Planner)
// Sets up routing, authentication protection, and theme
// ----------------------------------------------------------------------------

import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { RequireChurch } from './auth/RequireChurch';
import { RequireNoChurch } from './auth/RequireNoChurch';
import { RequireAdmin } from './auth/RequireAdmin';
import { Login } from './pages/Login';
import { VerifyEmail } from './pages/VerifyEmail';
import { ForgotPassword } from './pages/ForgotPassword';
import { CreateChurch, ChurchSetup } from './pages/onboarding/ChurchOnboarding';
import { AppShell } from './shell/AppShell';
import { ToastProvider } from './components/ui';
import { WorshipPlannerDashboard } from '@admin/worshipplanner/pages/WorshipPlannerDashboard';
import { Members } from '@admin/worshipplanner/pages/people/Members';
import { Songs } from '@admin/worshipplanner/pages/Songs';
import { SongDetail } from '@admin/worshipplanner/pages/SongDetail';
import { ChordLibrary } from '@admin/worshipplanner/pages/ChordLibrary';
import { ChordWatchProvider } from '@admin/worshipplanner/context/ChordWatchProvider';
import { featureFlags } from '@admin/shared/featureFlags';
import { Roster } from '@admin/worshipplanner/pages/Roster';
import { TeamAvailability } from '@admin/worshipplanner/pages/TeamAvailability';
import { ChurchSettings } from '@admin/worshipplanner/pages/account/ChurchSettings';
import { Teams } from '@admin/worshipplanner/pages/people/Teams';
import { InviteCodes } from '@admin/worshipplanner/pages/people/InviteCodes';
import { Requests } from '@admin/worshipplanner/pages/people/Requests';
import { Profile } from '@admin/worshipplanner/pages/account/Profile';
import { Plan } from '@admin/worshipplanner/pages/billing/Plan';
import { Billing } from '@admin/worshipplanner/pages/billing/Billing';
import { Developer } from '@admin/worshipplanner/pages/account/Developer';
import { Support } from '@admin/worshipplanner/pages/help/Support';
import { Setlists } from '@admin/worshipplanner/pages/Setlists';
import { Tips } from '@admin/worshipplanner/pages/help/Tips';
import { Notifications } from '@admin/worshipplanner/pages/inbox/Notifications';
import { AcceptInvite } from './pages/AcceptInvite';
import { SelectChurch } from './pages/SelectChurch';
import { MemberUpload } from './pages/MemberUpload';
import { ErrorBoundary } from './components/ErrorBoundary';
import { useEntitlements } from '@admin/shared/contexts/EntitlementsContext';

// Protected Route wrapper
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  // Show skeleton shell while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen bg-bg" aria-busy="true">
        {/* Top bar skeleton */}
        <div className="h-16 border-b border-line flex items-center gap-3 px-5">
          <span className="wp-skeleton w-[30px] h-[30px] rounded-[8px]" />
          <span className="wp-skeleton w-28 h-[34px] rounded-full" />
        </div>
        {/* Content skeleton */}
        <div className="max-w-[1280px] mx-auto px-5 pt-8 flex flex-col gap-3.5">
          <span className="wp-skeleton h-9 w-56 rounded-[10px]" />
          <span className="wp-skeleton h-40 rounded-[24px]" />
          <span className="wp-skeleton h-64 rounded-[24px]" />
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated, preserving where they were headed
  // so login can return them there (e.g. a member deep-linking to /upload).
  if (!user) {
    const dest = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?redirect=${dest}`} replace />;
  }

  return <>{children}</>;
};

// Chord AI route guard — non-entitled tiers are redirected to Subscription.
// Reads from the shared EntitlementsProvider so a tier change reflects here
// immediately (no full page reload needed).
const ChordLibraryRoute: React.FC = () => {
  const { canUseChordAi, isLoading } = useEntitlements();

  if (isLoading) return null;
  if (!canUseChordAi) return <Navigate to="/admin/subscription" replace />;
  return <ChordLibrary />;
};

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ChordWatchProvider>
        <BrowserRouter>
          <ToastProvider>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route
              path="/create-church"
              element={
                <RequireNoChurch>
                  <CreateChurch />
                </RequireNoChurch>
              }
            />
            <Route path="/invite/:code" element={<AcceptInvite />} />
            <Route path="/select-church" element={<SelectChurch />} />

            {/* Member Song Studio upload — any signed-in user; the page itself
                gates on church membership (create/join prompt when absent).
                Shares the Chord AI feature flag with the admin library. */}
            {featureFlags.chordAi && (
              <Route
                path="/upload"
                element={
                  <ProtectedRoute>
                    <MemberUpload />
                  </ProtectedRoute>
                }
              />
            )}

            {/* Protected Admin Routes */}
            <Route
              path="/admin/church-setup"
              element={
                <ProtectedRoute>
                  <RequireNoChurch>
                    <ChurchSetup />
                  </RequireNoChurch>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <RequireAdmin>
                    <RequireChurch>
                      <AppShell />
                    </RequireChurch>
                  </RequireAdmin>
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="dashboard" element={<WorshipPlannerDashboard />} />
              <Route path="members" element={<Members />} />
              <Route path="membership-requests" element={<Requests />} />
              <Route path="songs" element={<Songs />} />
              <Route path="songs/:songId" element={<SongDetail />} />
              {featureFlags.chordAi && <Route path="chord-library" element={<ChordLibraryRoute />} />}
              <Route path="team" element={<Teams />} />
              <Route path="invite" element={<InviteCodes />} />
              <Route path="roster" element={<Roster />} />
              <Route path="team-availability" element={<TeamAvailability />} />
              <Route path="subscription" element={<Plan />} />
              <Route path="billing" element={<Billing />} />
              <Route path="settings" element={<ChurchSettings />} />
              <Route path="developer-settings" element={<Developer />} />
              <Route path="profile" element={<Profile />} />
              <Route path="support" element={<Support />} />
              <Route path="tips" element={<Tips />} />
              <Route path="setlists" element={<Setlists />} />
              <Route path="notifications" element={<Notifications />} />
            </Route>

            {/* 404 */}
            <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
          </Routes>
          </ToastProvider>
        </BrowserRouter>
        </ChordWatchProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
